import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { formatTimeAgo } from '@/lib/format'
import { Skeleton } from '@/components/ui/Skeleton'
import { BlockStatusBadge } from './BlockStatusBadge'
import type { BlockSummary } from '../types'

interface Props {
  blocks: BlockSummary[]
  isLoading: boolean
  emptyMessage?: string
  /** Optional right-rail render slot per row (e.g. Approve buttons). */
  renderActions?: (block: BlockSummary) => React.ReactNode
}

/**
 * Compact shared table for blocks (active list + approval queue).
 * Each row is a link to the block detail; an `actions` slot renders to the
 * right of the chevron without intercepting the row navigation.
 */
export function BlockTable({ blocks, isLoading, emptyMessage, renderActions }: Props) {
  if (isLoading && blocks.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    )
  }
  if (blocks.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-fg">
        {emptyMessage ?? 'No blocks match.'}
      </p>
    )
  }
  return (
    <ul className="flex flex-col gap-1">
      {blocks.map((b) => (
        <li
          key={b.id}
          className="grid grid-cols-[minmax(0,7rem)_minmax(0,4rem)_minmax(0,1fr)_minmax(0,8rem)_minmax(0,5rem)_auto_1.25rem] items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm transition-colors hover:border-primary/40"
        >
          <Link
            to={`/blocks/${b.id}`}
            className="font-mono text-xs tabular-nums text-muted-fg hover:text-fg"
          >
            {b.block_number}
          </Link>
          <span className="text-xs uppercase text-muted-fg">{b.type}</span>
          <Link
            to={`/blocks/${b.id}`}
            className="min-w-0 truncate font-medium text-fg hover:underline"
            title={b.target?.display_label ?? '—'}
          >
            {b.target?.display_label ?? '—'}
          </Link>
          <span className="font-mono text-xs text-muted-fg">
            {b.requested_by?.display_name ?? '—'}
          </span>
          <span className="text-xs text-muted-fg">
            {formatTimeAgo(b.requested_at)}
          </span>
          <span className="flex items-center gap-2">
            <BlockStatusBadge status={b.status} />
            {b.is_regulatory && (
              <span className="rounded-sm bg-priority-critical/10 px-1 py-px text-[0.625rem] uppercase text-priority-critical">
                Reg
              </span>
            )}
            {renderActions && renderActions(b)}
          </span>
          <Link
            to={`/blocks/${b.id}`}
            className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
            aria-label={`Open block ${b.block_number}`}
          >
            <ChevronRight size={14} aria-hidden />
          </Link>
        </li>
      ))}
    </ul>
  )
}
